import { Injectable } from '@angular/core';
import { createState, Store, withProps } from '@ngneat/elf';
import {
  selectAllEntities,
  setEntities,
  withEntities,
  deleteEntities,
  hasEntity,
  upsertEntities,
  getEntity,
} from '@ngneat/elf-entities';

import { Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { freeze } from 'immer';

import { TrackerService } from '@app/shared/services/tracker.service';
import {
  BusinessRule,
  BusinessRulesState,
  BusinessRulesViewModel,
  initState,
  LoadOptions,
  Pagination,
  RuleTypes,
} from '../model';
import { BusinessRulesApiService } from '../services';
import { immutable } from '../../utils/immutable';
import { buildPredicateOperators } from '../utils';

type BusinessRulesStore = BusinessRulesState & {
  ids: string[];
  entities: Record<string, BusinessRule>;
};

@Injectable({ providedIn: 'root' }) // Explicitly register this Facade as a root singleton so DI is available for global modal dialog injections.
export class BusinessRuleFacade {
  public rules$: Observable<BusinessRule[]>;
  public vm$: Observable<BusinessRulesViewModel>;

  private store: Store;

  constructor(
    private businessRulesApiService: BusinessRulesApiService,
    private trackerService: TrackerService
  ) {
    const { state, config } = createState(
      withProps<BusinessRulesState>(initState()),
      withEntities<BusinessRule>()
    );
    this.store = new Store({ name: 'businessRules', state, config });

    this.rules$ = this.store.pipe(selectAllEntities());
    this.vm$ = this.store.pipe(this.buildViewModel());
  }

  public get currentRuleType() {
    return this.store.getValue().businessRuleType;
  }

  private get isLoading() {
    return this.store.getValue().isLoading;
  }

  private set isLoading(val: boolean) {
    this.updateStore((s) => (s.isLoading = val));
  }

  /**
   * Exposed method to load business rules for a specified organization
   */
  public loadRules(orgId: number, options?: LoadOptions): Subscription {
    return this.useTransaction(() => {
      const request$ = this.businessRulesApiService.loadRules(
        orgId,
        this.validateOptions(options)
      );

      return request$.subscribe((response) => {
        const { data: rules, ...overrides } = response;
        const { orderBy, isDescending } = options ?? {};

        this.updateStore((state) => {
          state.organizationId = orgId;
          state.businessRuleType =
            options?.businessRuleType ?? state.businessRuleType;
          state.searchTerm = options?.searchTerm ?? state.searchTerm;

          // ordering data does not come back from server
          state.ordering.orderBy = orderBy ?? state.ordering.orderBy;
          state.ordering.isDescending =
            isDescending ?? state.ordering.isDescending;

          state.pagination.currentPage = overrides.currentPage;
          state.pagination.pageSize = overrides.pageSize;
          state.pagination.totalCount = overrides.totalCount;
          state.pagination.totalPages = overrides.totalPages;
        }, rules);
      });
    });
  }
  public setRuleType(val: RuleTypes) {
    this.updateStore((s) => (s.businessRuleType = val));
  }

  /**
   * Exposed method to delete a rule
   */
  public deleteRule({ businessRuleId, id }: BusinessRule) {
    if (this.store.query(hasEntity(businessRuleId))) {
      const { organizationId, businessRuleType } =
        this.store.getValue() as BusinessRulesState;
      return this.useTransaction(() => {
        return this.businessRulesApiService
          .deleteRule(organizationId, businessRuleId)
          .pipe(
            tap(() => {
              this.trackerService.trackEventData({
                action: 'Org Business Rule Deleted',
                properties: {
                  ruleType: businessRuleType,
                },
              });
            })
          )
          .subscribe(() => {
            this.store.update(deleteEntities(id));
          });
      });
    }
  }

  public toggleEnabled(rule: BusinessRule) {
    const { organizationId } = this.store.getValue() as BusinessRulesState;
    return this.useTransaction(() => {
      return this.businessRulesApiService
        .setIsActive(organizationId, !rule.isActive, rule.businessRuleId)
        .pipe(
          tap(() => {
            this.store.update(
              upsertEntities({ ...rule, isActive: !rule.isActive })
            );
          })
        )
        .subscribe();
    });
  }

  public findRuleById(id: number): BusinessRule {
    return this.store.query(getEntity(id));
  }

  /**
   * Exposed method to update pagination
   */
  public updatePagination(pageOptions: Partial<Pagination>) {
    let { currentPage, pageSize } = pageOptions;

    const state: BusinessRulesState = this.store.getValue();

    currentPage = currentPage ?? state.pagination.currentPage;
    pageSize = pageSize ?? state.pagination.pageSize;
    const samePage = state.pagination.currentPage === currentPage;
    const sameSize = state.pagination.pageSize === pageSize;

    const hasValidPagination = currentPage > 0 && pageSize > 0;

    if ((!samePage || !sameSize) && hasValidPagination) {
      this.loadRules(state.organizationId, { currentPage, pageSize });
    }
  }

  /**
   * Exposed method to update the search term
   *
   * TODO: Note, this is not currently implemented in the backend, so we've commented out the search field.
   *       The code itself is safe to remain, the server side code will ignore any searchTerm passed along.
   */
  public updateSearchTerm(searchTerm: string) {
    const state: BusinessRulesState = this.store.getValue();
    const sameSearch = state.searchTerm === searchTerm;

    if (!sameSearch) {
      this.loadRules(state.organizationId, { searchTerm });
    }
  }

  /**
   * Exposed method to update ordering options
   */
  public updateOrdering(orderBy: string) {
    const state: BusinessRulesState = this.store.getValue();
    const currentlySortingOnColumn = orderBy === state.ordering.orderBy;
    const isDescending = currentlySortingOnColumn
      ? !state.ordering.isDescending
      : true;

    this.loadRules(state.organizationId, {
      orderBy,
      isDescending,
    });
  }

  public upsertRule(
    ruleUpdates: Partial<BusinessRule>,
    refreshPagination: boolean = true,
    existingRule?: BusinessRule
  ): Observable<BusinessRule> {
    const state: BusinessRulesState = this.store.getValue();

    ruleUpdates.organizationId = state.organizationId;
    ruleUpdates.predicateOperators = buildPredicateOperators(
      ruleUpdates.predicates
    );

    // if we're modifying a rule, merge the updates into the existing rule
    if (existingRule) {
      ruleUpdates = {
        ...existingRule,
        ...ruleUpdates,
      };
    }

    ruleUpdates.businessRuleType ??= state.businessRuleType; // set rule type if not previously set

    return this.businessRulesApiService.upsertRule(ruleUpdates).pipe(
      tap((serverRule: BusinessRule) => {
        if (existingRule || !refreshPagination) {
          this.store.update(upsertEntities(freeze(serverRule, true)));
        } else {
          if (refreshPagination) {
            // With current implementation, in order for the newly created rule to be visible in the result set, we need to sort the table in descending
            // order by the createDate, and without a search term.  This will ensure the newest rule will appear at the top of the table.
            this.loadRules(state.organizationId, {
              searchTerm: '',
              orderBy: 'createDate',
              isDescending: true,
              ...state.pagination,
              currentPage: 1,
              businessRuleType: state.businessRuleType,
            });
          }
        }
      }),
      tap((serverRule: BusinessRule) => {
        const action = `Org Business Rule ${
          existingRule ? 'Edited' : 'Created'
        }`;
        const ruleActions = serverRule.outcomes?.map(
          (outcome) => outcome.updateTypeResourceId
        );

        this.trackerService.trackEventData({
          action,
          properties: {
            ruleType: state.businessRuleType,
            ruleActions,
          },
        });
      })
    );
  }

  public validateName(ruleName: string): Observable<boolean> {
    const { organizationId, businessRuleType } =
      this.store.getValue() as BusinessRulesState;

    return this.businessRulesApiService.validateName(
      ruleName,
      organizationId,
      businessRuleType
    );
  }

  // use immutable to make all store state immutable
  private updateStore(
    fn: (state: BusinessRulesState) => void,
    rules?: BusinessRule[]
  ) {
    const frozenRules = freeze(rules, true);
    const frozenState = immutable(fn);
    const reducers = !!rules
      ? [setEntities(frozenRules), frozenState]
      : [frozenState];

    this.store.update(...reducers);
  }

  /**
   * Build an RXJS operator that will build the ViewModel when the store changes
   */
  private buildViewModel() {
    return map((store: BusinessRulesStore) => {
      const { entities, ids, ...state } = store;
      const rules = store.ids.map((id: string) => store.entities[id]);
      return {
        // Bind proxy API for easy use in Component HTML or JS
        updateSearchTerm: this.updateSearchTerm.bind(this),
        updatePagination: this.updatePagination.bind(this),
        updateOrdering: this.updateOrdering.bind(this),
        deleteRule: this.deleteRule.bind(this),
        upsertRule: this.upsertRule.bind(this),
        ...state,
        rules,
      } as BusinessRulesViewModel;
    });
  }

  /**
   * When starting a transaction, Auto toggle `isLoading` for remote Server calls
   */
  private useTransaction(fn: () => Subscription): Subscription {
    this.isLoading = true;
    const subscription = fn();

    subscription.add(() => (this.isLoading = false));

    return subscription;
  }

  private validateOptions(options?: LoadOptions): LoadOptions {
    const {
      searchTerm,
      businessRuleType,
      pagination: { currentPage, pageSize },
      ordering: { isDescending, orderBy },
    } = this.store.getValue();
    return {
      searchTerm,
      businessRuleType,
      currentPage,
      pageSize,
      isDescending,
      orderBy,
      ...options,
    };
  }
}
